import React from 'react'
import styled from 'styled-components'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import { TASK_STATUS_OPTIONS, taskStatusTint } from 'features/taskManagement/taskStatusConstants'
import SearchableSelect from 'components/SearchableSelect'

const statusOptions = [
  { value: 'all', label: 'All Tasks' },
  ...TASK_STATUS_OPTIONS,
  { value: 'overdue', label: 'Overdue' },
]

const assigneeOptions = [
  { value: 'me', label: 'My Tasks' },
  { value: 'all', label: 'All Tasks' },
  { value: 'created_by_me', label: 'Created by Me' },
  { value: 'pending_my_approval', label: 'Pending My Approval' }
]

const TaskFilters = ({
  filters,
  onFilterChange,
  onSearchChange,
  showFiltersPanel,
  setShowFiltersPanel
}) => {
  return (
    <FiltersWrapper>
      <SearchWrapper>
        <SearchIcon />
        <SearchInput
          type="text"
          placeholder="Search tasks..."
          value={filters.search || ''}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </SearchWrapper>

      <QuickFilters>
        {assigneeOptions.map((option) => (
          <QuickFilterChip
            key={option.value}
            $active={filters.assignedTo === option.value}
            onClick={() => onFilterChange({ assignedTo: option.value })}
          >
            {option.label}
          </QuickFilterChip>
        ))}
      </QuickFilters>

      <StatusFilters>
        <StatusFilterLabel>Status:</StatusFilterLabel>
        <StatusSelect
          value={filters.status || 'all'}
          onChange={(v) => onFilterChange({ status: v })}
          options={statusOptions.map((option) => ({ value: option.value, label: option.label, color: option.value === 'all' ? undefined : taskStatusTint(option.value) }))}
          ariaLabel='Filter by status'
        />

        <MoreFiltersButton
          onClick={() => setShowFiltersPanel(!showFiltersPanel)}
          $active={showFiltersPanel}
        >
          <FilterListIcon />
          <span>More</span>
        </MoreFiltersButton>
      </StatusFilters>
    </FiltersWrapper>
  )
}

export default TaskFilters

const FiltersWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 0;
    margin: 0 -16px 16px -16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.backgroundColor};
  border-radius: 8px;
  margin-bottom: 12px;

  svg {
    color: ${({ theme }) => theme.colors.grey[700]};
    font-size: 20px;
  }
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 15px;
  outline: none;
  color: ${({ theme }) => theme.colors.haiti};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[700]};
  }
`

const QuickFilters = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    margin: 0 -12px 12px -12px;
    padding: 0 12px 12px 12px;
  }
`

const QuickFilterChip = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.mediumGrey};
  background: ${({ $active, theme }) =>
    $active ? `${theme.colors.primary}10` : 'white'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.haiti};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}08;
  }
`

const StatusFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`

const StatusFilterLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey[800]};

  @media (max-width: 768px) {
    display: none;
  }
`

const StatusSelect = styled(SearchableSelect)`
  flex: 1;
  max-width: 200px;

  @media (max-width: 768px) {
    flex: 1;
    max-width: none;
  }
`

const MoreFiltersButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.mediumGrey};
  border-radius: 8px;
  background: ${({ $active, theme }) =>
    $active ? `${theme.colors.primary}10` : 'white'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.haiti};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`
